import React, { useState } from 'react';
import { Copy, Check, RefreshCw, Pencil, GitBranch } from 'lucide-react';
import { cn } from '../../lib/utils';

// ═══════════════════════════════════════════════════════════════════════════════
// CHAT MESSAGE ACTIONS
//
// The hover bar under a message. Replaces the two duplicated copy buttons that
// used to sit between the avatar and the bubble.
//
// Visibility is pure CSS so it costs nothing per frame. `max-md:opacity-100`
// keeps the bar reachable on touch, where there is no hover state at all.
// ═══════════════════════════════════════════════════════════════════════════════

const actionClass =
    'flex items-center gap-1.5 rounded-lg px-2 py-1 font-mono text-[10px] font-bold uppercase ' +
    'tracking-[0.14em] text-muted-foreground transition-colors duration-150 ' +
    'hover:bg-white/10 hover:text-foreground focus-visible:outline-none ' +
    'focus-visible:ring-1 focus-visible:ring-primary/40';

export function ChatMessageActions({
    content,
    isUser,
    isLastUserMessage = true,
    onRetry,
    onEdit,
    onBranchAndEdit,
    onEditAndResend,
    className,
}) {
    const [copied, setCopied] = useState(false);

    const copy = () => {
        navigator.clipboard.writeText(content || '');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const editHandler = onEdit || onEditAndResend;

    return (
        <div
            className={cn(
                'flex items-center gap-0.5 opacity-0 transition-opacity duration-150',
                'group-hover/msg:opacity-100 focus-within:opacity-100 max-md:opacity-100',
                isUser && 'justify-end',
                className
            )}
        >
            <button onClick={copy} className={actionClass} title="Copy message" data-testid="chat-copy-message">
                {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                {copied ? 'Copied' : 'Copy'}
            </button>

            {!isUser && onRetry && (
                // Replaces this answer in place — the old turn is dropped from
                // the session first, so the transcript never shows two attempts
                // at the same prompt.
                <button onClick={onRetry} className={actionClass} title="Replace this answer" data-testid="chat-retry">
                    <RefreshCw className="h-3 w-3" />
                    Retry
                </button>
            )}

            {isUser && isLastUserMessage && editHandler && (
                <button
                    onClick={editHandler}
                    className={actionClass}
                    title="Edit message"
                    data-testid="chat-edit-resend"
                >
                    <Pencil className="h-3 w-3" />
                    Edit
                </button>
            )}

            {isUser && !isLastUserMessage && onBranchAndEdit && (
                <button
                    onClick={onBranchAndEdit}
                    className={actionClass}
                    title="Branch conversation from this message and edit"
                    data-testid="chat-branch-edit"
                >
                    <GitBranch className="h-3 w-3" />
                    Branch and edit
                </button>
            )}
        </div>
    );
}

export default ChatMessageActions;
